import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchUserFavorites, 
  saveUserFavorites, 
  selectFavorites, 
  selectFavoritesLoading,
  selectFavoritesError 
} from '../redux/slices/favoritesSlice';

export const useFavorites = (userId) => {
  const dispatch = useDispatch();
  const favorites = useSelector(selectFavorites);
  const loading = useSelector(selectFavoritesLoading);
  const error = useSelector(selectFavoritesError);

  // Load favorites
  const loadFavorites = useCallback(async () => {
    if (!userId) return;
    
    try {
      await dispatch(fetchUserFavorites(userId)).unwrap();
    } catch (error) {
      console.error('Error loading favorites:', error);
      throw error;
    }
  }, [dispatch, userId]);

  // Save favorites
  const saveFavorites = useCallback(async (newFavorites) => {
    if (!userId) return;
    
    try {
      await dispatch(saveUserFavorites({ userId, favorites: newFavorites })).unwrap();
    } catch (error) {
      console.error('Error saving favorites:', error);
      throw error;
    }
  }, [dispatch, userId]);

  // Add a favorite
  const addFavorite = useCallback(async (favorite) => {
    if (!userId) return;
    
    const newFavorites = [...favorites, favorite];
    await saveFavorites(newFavorites);
  }, [userId, favorites, saveFavorites]);

  // Remove a favorite
  const removeFavorite = useCallback(async (favoriteId) => {
    if (!userId) return;
    
    const newFavorites = favorites.filter(fav => fav.id !== favoriteId);
    await saveFavorites(newFavorites);
  }, [userId, favorites, saveFavorites]);

  // Update a favorite
  const updateFavorite = useCallback(async (favoriteId, updates) => {
    if (!userId) return;
    
    const newFavorites = favorites.map(fav => 
      fav.id === favoriteId ? { ...fav, ...updates } : fav
    );
    await saveFavorites(newFavorites);
  }, [userId, favorites, saveFavorites]);

  // Check if an item is favorited
  const isFavorited = useCallback((itemId) => {
    return favorites.some(fav => fav.id === itemId);
  }, [favorites]);

  return {
    favorites,
    loading,
    error,
    loadFavorites,
    saveFavorites,
    addFavorite,
    removeFavorite,
    updateFavorite,
    isFavorited
  };
};
